import React, { useState, useEffect } from "react";
import { addMonths, addDays, format } from "date-fns";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from "@/components/ui/switch";
import { Loader2, Check, Calendar, Repeat, ChevronsUpDown, AlertCircle, FileText, Wallet, LayoutGrid } from "lucide-react";
import LeitorCodigoBarras from "./LeitorCodigoBarras";
import PartnerSearchPrecision from "../shared/PartnerSearchPrecision";

const categorias = [
  { value: "folha_pagamento", label: "Folha de Pagamento", icon: "👥" },
  { value: "impostos", label: "Impostos e Taxas", icon: "🏛️" },
  { value: "energia", label: "Energia Elétrica", icon: "⚡" },
  { value: "agua", label: "Água", icon: "💧" },
  { value: "aluguel", label: "Aluguel/Arrendamento", icon: "🏠" },
  { value: "financiamento", label: "Financiamento/Empréstimo", icon: "🏦" },
  { value: "insumos", label: "Insumos Agrícolas", icon: "🌱" },
  { value: "combustivel", label: "Combustível", icon: "⛽" },
  { value: "manutencao", label: "Manutenção", icon: "🔧" },
  { value: "veterinario", label: "Veterinário", icon: "🐄" },
  { value: "transporte", label: "Transporte/Frete", icon: "🚛" },
  { value: "seguro", label: "Seguros", icon: "🛡️" },
  { value: "consultoria", label: "Consultoria/Assessoria", icon: "📋" },
  { value: "outros", label: "Outros", icon: "📦" }
];

export default function ContaForm({ conta, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  const [formData, setFormData] = useState({
    descricao: "",
    valor: "",
    data_vencimento: "",
    categoria: "",
    fornecedor: "",
    status: "pendente",
    recorrente: false,
    frequencia_recorrencia: "mensal",
    lembrete_dias: 3,
    forma_pagamento: "boleto",
    codigo_barras: "",
    observacoes: "",
    boleto_url: "",
    nota_fiscal_url: "",
    safra_id: "",
    parcela_atual: "",
    total_parcelas: ""
  });
  const [baixaData, setBaixaData] = useState({
    data_pagamento: "",
    juros: "",
    multa: "",
    desconto: "",
    conta_bancaria_id: "",
    comprovante_url: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploadingBoleto, setUploadingBoleto] = useState(false);
  const [uploadingNota, setUploadingNota] = useState(false);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState({
    descricao: false,
    valor: false,
    categoria: false,
    data_vencimento: false
  });
  const [errors, setErrors] = useState({});

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId }),
    enabled: !!propriedadeId
  });

  const { data: parceiros = [] } = useQuery({
    queryKey: ['parceiros', propriedadeId],
    queryFn: () => agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId }),
    enabled: !!propriedadeId
  });

  useEffect(() => {
    setSubmitAttempted(false);
    setTouched({
      descricao: false,
      valor: false,
      categoria: false,
      data_vencimento: false
    });
    setErrors({});
    if (conta) {
      setFormData({
        descricao: conta.descricao || "",
        valor: conta.valor?.toString() || "",
        data_vencimento: conta.data_vencimento || "",
        categoria: conta.categoria || "",
        fornecedor: conta.fornecedor || "",
        status: conta.status === "atrasado" ? "pendente" : (conta.status || "pendente"),
        recorrente: conta.recorrente || false,
        frequencia_recorrencia: conta.frequencia_recorrencia || "mensal",
        lembrete_dias: conta.lembrete_dias || 3,
        forma_pagamento: conta.forma_pagamento || "boleto",
        codigo_barras: conta.codigo_barras || "",
        observacoes: conta.observacoes || "",
        boleto_url: conta.boleto_url || "",
        nota_fiscal_url: conta.nota_fiscal_url || "",
        safra_id: conta.safra_id || "",
        parcela_atual: conta.parcela_atual?.toString() || "",
        total_parcelas: conta.total_parcelas?.toString() || ""
      });
      setBaixaData({
        data_pagamento: conta.data_pagamento || "",
        juros: conta.juros?.toString() || "",
        multa: conta.multa?.toString() || "",
        desconto: conta.desconto?.toString() || "",
        conta_bancaria_id: conta.conta_bancaria_id || "",
        comprovante_url: conta.comprovante_url || ""
      });
    }
  }, [conta]);

  const validateDescricao = (value) => {
    if (!value || !String(value).trim()) return "Descrição é obrigatória";
    return "";
  };

  const validateValor = (value) => {
    if (value === "" || value === null || value === undefined) return "Valor é obrigatório";
    const num = parseFloat(String(value).replace(",", "."));
    if (Number.isNaN(num) || num <= 0) return "Valor deve ser maior que zero";
    return "";
  };

  const validateCategoria = (value) => {
    if (!value) return "Categoria é obrigatória";
    const exists = categorias.some((cat) => cat.value === value);
    if (!exists) return "Categoria inválida";
    return "";
  };

  const validateDataVencimento = (value) => {
    if (!value) return "Data de vencimento é obrigatória";
    return "";
  };

  const getErrors = (data) => {
    const next = {};
    const descricaoError = validateDescricao(data.descricao);
    const valorError = validateValor(data.valor);
    const categoriaError = validateCategoria(data.categoria);
    const vencError = validateDataVencimento(data.data_vencimento);
    if (descricaoError) next.descricao = descricaoError;
    if (valorError) next.valor = valorError;
    if (categoriaError) next.categoria = categoriaError;
    if (vencError) next.data_vencimento = vencError;
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const nextErrors = getErrors(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      try {
        await agrogestao.access.logAudit({
          propriedade_id: propriedadeId,
          acao: "envio_incompleto",
          table: "contas_pagar",
          status: "blocked",
          details: {
            campos_invalidos: Object.keys(nextErrors)
          }
        });
      } catch { }
      return;
    }
    setSaving(true);

    const statusNormalizado = formData.status === "atrasado" ? "pendente" : formData.status;
    const data = {
      ...formData,
      status: statusNormalizado,
      propriedade_id: propriedadeId,
      valor: parseFloat(formData.valor) || 0,
      lembrete_dias: parseInt(formData.lembrete_dias) || 3,
      parcela_atual: formData.parcela_atual ? parseInt(formData.parcela_atual) : null,
      total_parcelas: formData.total_parcelas ? parseInt(formData.total_parcelas) : null,
      safra_id: formData.safra_id ? formData.safra_id : null,
      data_pagamento: baixaData.data_pagamento || null,
      juros: baixaData.juros ? parseFloat(baixaData.juros) : 0,
      multa: baixaData.multa ? parseFloat(baixaData.multa) : 0,
      desconto: baixaData.desconto ? parseFloat(baixaData.desconto) : 0,
      conta_bancaria_id: baixaData.conta_bancaria_id || null,
      comprovante_url: baixaData.comprovante_url || null
    };

    if (conta) {
      await agrogestao.entities.ContaPagar.update(conta.id, data);
    } else {
      const parcelas = data.total_parcelas && data.total_parcelas > 1 ? data.total_parcelas : 1;
      if (parcelas === 1) {
        await agrogestao.entities.ContaPagar.create({ ...data, parcela_atual: data.parcela_atual || 1, total_parcelas: 1 });
      } else {
        const baseDate = new Date(formData.data_vencimento);
        const freq = formData.frequencia_recorrencia || 'mensal';
        const valorTotal = data.valor;
        const valorParcela = Math.round((valorTotal / parcelas) * 100) / 100;
        let acumulado = 0;
        for (let i = 1; i <= parcelas; i++) {
          let due;
          if (freq === 'semanal') due = addDays(baseDate, 7 * (i - 1));
          else if (freq === 'quinzenal') due = addDays(baseDate, 15 * (i - 1));
          else if (freq === 'bimestral') due = addMonths(baseDate, 2 * (i - 1));
          else if (freq === 'trimestral') due = addMonths(baseDate, 3 * (i - 1));
          else if (freq === 'semestral') due = addMonths(baseDate, 6 * (i - 1));
          else if (freq === 'anual') due = addMonths(baseDate, 12 * (i - 1));
          else due = addMonths(baseDate, 1 * (i - 1));
          let v = valorParcela;
          acumulado += valorParcela;
          if (i === parcelas) {
            v = Math.round((valorTotal - (valorParcela * (parcelas - 1))) * 100) / 100;
          }
          await agrogestao.entities.ContaPagar.create({
            ...data,
            descricao: `${formData.descricao} (${i}/${parcelas})`,
            valor: v,
            data_vencimento: format(due, 'yyyy-MM-dd'),
            parcela_atual: i,
            total_parcelas: parcelas
          });
        }
      }
    }

    setSaving(false);
    onSuccess();
  };

  const handleDadosExtraidos = (dados) => {
    setFormData(prev => ({
      ...prev,
      valor: dados.valor ? dados.valor.toString() : prev.valor,
      data_vencimento: dados.data_vencimento || prev.data_vencimento,
      fornecedor: dados.fornecedor || prev.fornecedor,
      categoria: dados.categoria || prev.categoria,
      codigo_barras: dados.codigo_barras || dados.linha_digitavel || prev.codigo_barras
    }));
  };

  const handleBoletoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBoleto(true);
    try {
      const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
      setFormData(prev => ({ ...prev, boleto_url: file_url }));
    } finally {
      setUploadingBoleto(false);
      e.target.value = "";
    }
  };

  const handleNotaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNota(true);
    try {
      const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
      setFormData(prev => ({ ...prev, nota_fiscal_url: file_url }));
    } finally {
      setUploadingNota(false);
      e.target.value = "";
    }
  };

  const handleComprovanteUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingComprovante(true);
    try {
      const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
      setBaixaData(prev => ({ ...prev, comprovante_url: file_url }));
    } finally {
      setUploadingComprovante(false);
      e.target.value = "";
    }
  };

  const { data: contasBancarias = [] } = useQuery({
    queryKey: ['contas_bancarias', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaBancaria.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const parceirosFiltrados = parceiros.filter(p => (p.tipo || "outro") === "fornecedor");
  const parceiroSelecionado = parceirosFiltrados.find((p) => {
    const nome = (p.nome_razao || p.razao_social || p.nome || "").trim();
    return nome && nome === formData.fornecedor;
  });
  const showDescricaoError = (touched.descricao || submitAttempted) && errors.descricao;
  const showValorError = (touched.valor || submitAttempted) && errors.valor;
  const showCategoriaError = (touched.categoria || submitAttempted) && errors.categoria;
  const showVencimentoError = (touched.data_vencimento || submitAttempted) && errors.data_vencimento;
  const hasBlockingErrors = Object.keys(getErrors(formData)).length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Leitor de Código de Barras */}
      {!conta && (
        <LeitorCodigoBarras onDadosExtraidos={handleDadosExtraidos} />
      )}

      {/* Seção 1: Dados da Despesa */}
      <div className="bg-stone-50/50 p-4 rounded-xl border border-stone-100 space-y-4">
        <div className="flex items-center gap-2 mb-2 text-stone-800 font-semibold text-sm uppercase tracking-wider">
          <FileText className="w-4 h-4 text-emerald-600" />
          Dados da Despesa
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-2">
            <Label>Descrição *</Label>
            <Input
              placeholder="Ex: Parcela trator, Energia outubro..."
              value={formData.descricao}
              onChange={(e) => {
                const next = { ...formData, descricao: e.target.value };
                setFormData(next);
                if (submitAttempted || touched.descricao) {
                  setErrors((prev) => ({ ...prev, descricao: validateDescricao(next.descricao) }));
                }
              }}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, descricao: true }));
                setErrors((prev) => ({ ...prev, descricao: validateDescricao(formData.descricao) }));
              }}
              required
              className={showDescricaoError ? "border-red-500" : ""}
            />
            {showDescricaoError && (
              <span className="text-[10px] text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {errors.descricao}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <Label>Valor (R$) *</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="0,00"
              value={formData.valor}
              onChange={(e) => {
                const next = { ...formData, valor: e.target.value };
                setFormData(next);
                if (submitAttempted || touched.valor) {
                  setErrors((prev) => ({ ...prev, valor: validateValor(next.valor) }));
                }
              }}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, valor: true }));
                setErrors((prev) => ({ ...prev, valor: validateValor(formData.valor) }));
              }}
              required
              className={`font-semibold ${showValorError ? "border-red-500" : ""}`}
            />
            {showValorError && (
              <span className="text-[10px] text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {errors.valor}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Categoria *</Label>
            <Select
              value={formData.categoria}
              onValueChange={(v) => {
                const next = { ...formData, categoria: v };
                setFormData(next);
                setTouched((prev) => ({ ...prev, categoria: true }));
                if (submitAttempted || touched.categoria) {
                  setErrors((prev) => ({ ...prev, categoria: validateCategoria(v) }));
                }
              }}
              required
            >
              <SelectTrigger className={showCategoriaError ? "border-red-500" : ""}>
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {categorias.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <span className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      {cat.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showCategoriaError && (
              <span className="text-[10px] text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {errors.categoria}
              </span>
            )}
          </div>

          {safras.length > 0 && (
            <div className="space-y-2">
              <Label>Safra (opcional)</Label>
              <Select
                value={formData.safra_id}
                onValueChange={(v) => setFormData({ ...formData, safra_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vincular à safra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {safras.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Seção 2: Pagamento e Fornecedor */}
      <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2 mb-2 text-stone-800 font-semibold text-sm uppercase tracking-wider">
          <Wallet className="w-4 h-4 text-blue-600" />
          Pagamento e Fornecedor
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Fornecedor</Label>
            <PartnerSearchPrecision
              value={formData.fornecedor}
              onChange={(val) => setFormData(prev => ({ ...prev, fornecedor: val }))}
              tipo="fornecedor"
              placeholder="Buscar fornecedor..."
            />
          </div>
          <div className="space-y-2">
            <Label>Data de Vencimento *</Label>
            <Input
              type="date"
              value={formData.data_vencimento}
              onChange={(e) => {
                const next = { ...formData, data_vencimento: e.target.value };
                setFormData(next);
                if (submitAttempted || touched.data_vencimento) {
                  setErrors((prev) => ({ ...prev, data_vencimento: validateDataVencimento(next.data_vencimento) }));
                }
              }}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, data_vencimento: true }));
                setErrors((prev) => ({ ...prev, data_vencimento: validateDataVencimento(formData.data_vencimento) }));
              }}
              required
              className={showVencimentoError ? "border-red-500" : ""}
            />
            {showVencimentoError && (
              <span className="text-[10px] text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3 h-3" />
                {errors.data_vencimento}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Forma de Pagamento</Label>
            <Select
              value={formData.forma_pagamento}
              onValueChange={(v) => setFormData({ ...formData, forma_pagamento: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="boleto">Boleto</SelectItem>
                <SelectItem value="pix">PIX</SelectItem>
                <SelectItem value="transferencia">Transferência</SelectItem>
                <SelectItem value="debito_automatico">Débito Automático</SelectItem>
                <SelectItem value="cartao">Cartão</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="dinheiro">Dinheiro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Parcela</Label>
              <Input
                type="number"
                placeholder="Ex: 1"
                value={formData.parcela_atual}
                onChange={(e) => setFormData({ ...formData, parcela_atual: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Total</Label>
              <Input
                type="number"
                placeholder="Ex: 12"
                value={formData.total_parcelas}
                onChange={(e) => setFormData({ ...formData, total_parcelas: e.target.value })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Seção 3: Detalhes e Recorrência */}
      <div className="p-4 bg-stone-50 rounded-xl space-y-4 border border-stone-200">
        <div className="flex items-center gap-2 mb-2 text-stone-800 font-semibold text-sm uppercase tracking-wider">
          <LayoutGrid className="w-4 h-4 text-indigo-600" />
          Opções e Recorrência
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Repeat className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-medium text-sm">Conta Recorrente</p>
                  <p className="text-[10px] text-stone-500 uppercase">Repetir automaticamente</p>
                </div>
              </div>
              <Switch
                checked={formData.recorrente}
                onCheckedChange={(checked) => setFormData({ ...formData, recorrente: checked })}
              />
            </div>

            {formData.recorrente && (
              <div className="grid grid-cols-1 gap-3 pt-2">
                <div className="space-y-1">
                  <Label className="text-xs">Frequência</Label>
                  <Select
                    value={formData.frequencia_recorrencia}
                    onValueChange={(v) => setFormData({ ...formData, frequencia_recorrencia: v })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="quinzenal">Quinzenal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="bimestral">Bimestral</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-sm">Lembrete</p>
                <div className="flex items-center gap-2 mt-1">
                  <Select
                    value={formData.lembrete_dias.toString()}
                    onValueChange={(v) => setFormData({ ...formData, lembrete_dias: parseInt(v) })}
                  >
                    <SelectTrigger className="w-20 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 5, 7, 15].map(d => (
                        <SelectItem key={d} value={d.toString()}>{d} dia{d > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-[10px] text-stone-500 font-medium uppercase">antes do vencimento</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-stone-200">
          <Label className="text-xs font-semibold uppercase text-stone-500">Status Atual</Label>
          <Select
            value={formData.status}
            onValueChange={(v) => setFormData({ ...formData, status: v })}
          >
            <SelectTrigger className="mt-1 h-9 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="agendado">Agendado (Débito Automático)</SelectItem>
              <SelectItem value="pago">Já Pago (Realizar Baixa)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.status === 'pago' && (
          <div className="mt-4 p-4 rounded-xl border-2 border-emerald-100 bg-emerald-50/30 space-y-4">
            <p className="font-bold text-emerald-800 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" /> Informações da Baixa
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Data do Pagamento</Label>
                <Input type="date" className="h-8 text-xs" value={baixaData.data_pagamento} onChange={(e) => setBaixaData({ ...baixaData, data_pagamento: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Juros (R$)</Label>
                <Input type="number" step="0.01" className="h-8 text-xs" value={baixaData.juros} onChange={(e) => setBaixaData({ ...baixaData, juros: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Multa (R$)</Label>
                <Input type="number" step="0.01" className="h-8 text-xs" value={baixaData.multa} onChange={(e) => setBaixaData({ ...baixaData, multa: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Desconto (R$)</Label>
                <Input type="number" step="0.01" className="h-8 text-xs" value={baixaData.desconto} onChange={(e) => setBaixaData({ ...baixaData, desconto: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Conta Bancária</Label>
                <Select value={baixaData.conta_bancaria_id} onValueChange={(v) => setBaixaData({ ...baixaData, conta_bancaria_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-8 text-xs bg-white">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {contasBancarias.map(cb => (
                      <SelectItem key={cb.id} value={cb.id}>{cb.banco} • {cb.numero}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Comprovante</Label>
                <Input type="file" className="h-8 text-[10px]" accept="application/pdf,image/*" onChange={handleComprovanteUpload} disabled={uploadingComprovante} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Seção 4: Documentos e Notas */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Código de Barras / Linha Digitável</Label>
          <Input
            placeholder="Cole aqui o código de barras do boleto"
            value={formData.codigo_barras}
            onChange={(e) => setFormData({ ...formData, codigo_barras: e.target.value })}
            className="font-mono text-xs"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm">Boleto (PDF/IMG)</Label>
            <div className="flex items-center gap-2">
              <Input type="file" className="text-xs h-9" accept="application/pdf,image/*" onChange={handleBoletoUpload} disabled={uploadingBoleto} />
              {formData.boleto_url && (
                <Button size="icon" variant="outline" className="shrink-0" asChild>
                  <a href={formData.boleto_url} target="_blank" rel="noreferrer">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </a>
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Nota Fiscal (PDF/IMG)</Label>
            <div className="flex items-center gap-2">
              <Input type="file" className="text-xs h-9" accept="application/pdf,image/*" onChange={handleNotaUpload} disabled={uploadingNota} />
              {formData.nota_fiscal_url && (
                <Button size="icon" variant="outline" className="shrink-0" asChild>
                  <a href={formData.nota_fiscal_url} target="_blank" rel="noreferrer">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Observações</Label>
          <Textarea
            placeholder="Informações adicionais relevantes para este lançamento..."
            value={formData.observacoes}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            rows={2}
            className="resize-none"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 sticky bottom-0 bg-white py-2 border-t mt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={saving || hasBlockingErrors}
          className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F] shadow-lg"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              {conta ? "Salvar Alterações" : "Confirmar Lançamento"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export { categorias };
